import { Control } from "react-hook-form";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Property } from "@/types/types";

interface PropertySelectorProps {
  control: Control<any>;
  properties: Property[];
  disabled?: boolean;
  onPropertyChange?: (propertyId: string) => void;
  required?: boolean;
}

/**
 * Reusable PropertySelector component
 * Single responsibility: Handle property selection from a list
 */
export function PropertySelector({
  control,
  properties,
  disabled = false,
  onPropertyChange,
  required = true,
}: PropertySelectorProps) {
  return (
    <FormField
      control={control}
      name="property"
      render={({ field }) => (
        <FormItem>
          <FormLabel className="text-base">
            Property {required && "*"}
          </FormLabel>
          <Select
            onValueChange={(value) => {
              const propertyId = value === "none" ? "" : value;
              field.onChange(propertyId);
              onPropertyChange?.(propertyId);
            }}
            value={field.value || "none"}
            disabled={disabled}
          >
            <FormControl>
              <SelectTrigger className="w-full h-11">
                <SelectValue placeholder="Select a property" />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              {properties.length === 0 ? (
                <div className="p-3 text-sm text-muted-foreground text-center">
                  No properties available
                </div>
              ) : (
                properties.map((property) => (
                  <SelectItem key={property.id} value={property.id}>
                    <div className="flex flex-col py-1">
                      <span className="font-medium">{property.title}</span>
                      <span className="text-xs text-muted-foreground">
                        {property.address}
                      </span>
                    </div>
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
